import React from 'react';
import { Banknote, FileText, Loader2, Search } from 'lucide-react';
import type { DocumentType, Invoice } from '../../types';
import { openAmountCents } from '../../services/invoiceLogic';
import { formatCents } from '../../services/money';
import {
    de,
    DOCUMENT_TYPE_FILTERS,
    DOCUMENT_TYPE_SHORT,
    retentionHint,
    STATUS_FILTERS,
    type Filter,
} from './invoicesViewShared';
import { SortableTh } from './invoicesViewParts';
import { BillingModeChip } from './BillingModeChip';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import { canMarkPaid } from './paymentBooking';
import type { KnopfRecht } from './rechnungRechte';
import type { InvoiceListSort, InvoiceListTotals } from './invoiceListLogic';

/**
 * Die Belegliste der Rechnungsuebersicht: Filterleiste und Tabelle.
 *
 * Reine Anzeige. Gefiltert, sortiert und geblaettert wird in der Ansicht
 * (invoiceListLogic); hier kommt nur an, was zu zeigen ist, und jede Aenderung
 * geht ueber die Rueckrufe zurueck.
 */

export interface InvoiceListFilterBarProps {
    search: string;
    onSearchChange: (search: string) => void;
    filter: Filter;
    onFilterChange: (filter: Filter) => void;
    customerFilter: string;
    onCustomerFilterChange: (customerId: string) => void;
    /** Kunden-ID und Name, wie die Auswahl sie zeigt. */
    customerOptions: Array<[string, string]>;
    typeFilter: DocumentType | 'all';
    onTypeFilterChange: (type: DocumentType | 'all') => void;
    dateFrom: string;
    onDateFromChange: (date: string) => void;
    dateTo: string;
    onDateToChange: (date: string) => void;
    filtersActive: boolean;
    onResetFilters: () => void;
}

export const InvoiceListFilterBar: React.FC<InvoiceListFilterBarProps> = ({
    search,
    onSearchChange,
    filter,
    onFilterChange,
    customerFilter,
    onCustomerFilterChange,
    customerOptions,
    typeFilter,
    onTypeFilterChange,
    dateFrom,
    onDateFromChange,
    dateTo,
    onDateToChange,
    filtersActive,
    onResetFilters,
}) => (
    /* Filterleiste in derselben Sprache wie die Angebote: eine weisse
        Karte, Pillen statt Auswahlfeld. Der Status ist die Frage, die
        man am haeufigsten stellt — er gehoert auf einen Blick sichtbar
        und mit einem Klick erreichbar, nicht hinter ein Aufklappmenue. */
    <div data-glass="filterleiste" className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[220px] flex-1">
                <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-300" />
                <input
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Suchen: Nummer, Kunde…"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-10 pr-4 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                />
            </div>
            {STATUS_FILTERS.map((f) => (
                <button
                    key={f.key}
                    type="button"
                    onClick={() => onFilterChange(f.key)}
                    className={`rounded-xl px-4 py-2 text-sm font-bold transition-all ${
                        filter === f.key
                            ? 'bg-blue-600 text-white shadow'
                            : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                    }`}
                >
                    {f.label}
                </button>
            ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            <select
                value={customerFilter}
                onChange={(e) => onCustomerFilterChange(e.target.value)}
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
            >
                <option value="all">Alle Kunden</option>
                {customerOptions.map(([id, name]) => (
                    <option key={id} value={id}>
                        {name}
                    </option>
                ))}
            </select>
            <select
                value={typeFilter}
                onChange={(e) => onTypeFilterChange(e.target.value as typeof typeFilter)}
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
            >
                <option value="all">Alle Belegarten</option>
                {DOCUMENT_TYPE_FILTERS.map(([value, label]) => (
                    <option key={value} value={value}>
                        {label}
                    </option>
                ))}
            </select>
            <label className="flex items-center gap-2 text-xs text-slate-500">
                Belegdatum
                <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => onDateFromChange(e.target.value)}
                    className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                />
                bis
                <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => onDateToChange(e.target.value)}
                    className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                />
            </label>
            {filtersActive && (
                <button
                    type="button"
                    onClick={onResetFilters}
                    className="ml-auto rounded-xl px-3 py-2 text-sm font-bold text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                >
                    Filter zurücksetzen
                </button>
            )}
        </div>
    </div>
);

export interface InvoiceListTableProps {
    loading: boolean;
    /** Alle geladenen Belege — entscheidet den Leerzustand und "gefiltert aus". */
    invoiceCount: number;
    /** Treffer nach allen Filtern, ueber alle Seiten. */
    visibleCount: number;
    /** Die Zeilen der aktuellen Seite. */
    pageItems: Invoice[];
    /** Summen ueber ALLE Treffer, nicht nur die Seite. */
    totals: InvoiceListTotals;
    filtersActive: boolean;
    onResetFilters: () => void;
    sort: InvoiceListSort;
    onSort: (sort: InvoiceListSort) => void;
    /** Der Beleg, dessen Positionen gerade nachgeladen werden. */
    opening: string | null;
    onOpenInvoice: (id: string) => Promise<void>;
    onMarkPaid: (inv: Invoice) => void;
    /** Recht "Zahlung buchen" fuer den Knopf an der Zeile. */
    zahlungen: KnopfRecht;
    customerNameOf: (inv: Invoice) => string;
    projectLabelOf: (inv: Invoice) => string;
    today: string;
    pageSize: number;
    onPageSizeChange: (pageSize: number) => void;
    currentPage: number;
    pageCount: number;
    onPageChange: (page: number) => void;
}

export const InvoiceListTable: React.FC<InvoiceListTableProps> = ({
    loading,
    invoiceCount,
    visibleCount,
    pageItems,
    totals,
    filtersActive,
    onResetFilters,
    sort,
    onSort,
    opening,
    onOpenInvoice,
    onMarkPaid,
    zahlungen,
    customerNameOf,
    projectLabelOf,
    today,
    pageSize,
    onPageSizeChange,
    currentPage,
    pageCount,
    onPageChange,
}) =>
    loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Rechnungen werden geladen …
        </div>
    ) : visibleCount === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-slate-100 bg-white py-20 text-center text-slate-400">
            <div className="mx-auto mb-4 w-fit rounded-full bg-slate-50 p-4">
                <FileText className="h-10 w-10 opacity-20" />
            </div>
            <p className="font-bold">
                {invoiceCount === 0 ? 'Noch keine Rechnungen' : 'Keine Treffer'}
            </p>
            <p className="text-xs">
                {invoiceCount === 0
                    ? 'Über „Neue Rechnung" lassen sich offene Positionen eines Projekts abrechnen.'
                    : 'Kein Beleg passt zu dieser Auswahl.'}
            </p>
            {filtersActive && invoiceCount > 0 && (
                <button
                    type="button"
                    onClick={onResetFilters}
                    className="mt-4 rounded-xl bg-slate-50 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-100"
                >
                    Filter zurücksetzen
                </button>
            )}
        </div>
    ) : (
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] border-collapse text-left text-sm">
                <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        <SortableTh sortKey="number" sort={sort} onSort={onSort}>
                            Nummer
                        </SortableTh>
                        <SortableTh sortKey="customer" sort={sort} onSort={onSort}>
                            Kunde
                        </SortableTh>
                        <SortableTh sortKey="project" sort={sort} onSort={onSort}>
                            Projekt
                        </SortableTh>
                        <SortableTh sortKey="date" sort={sort} onSort={onSort}>
                            Datum
                        </SortableTh>
                        <SortableTh sortKey="due" sort={sort} onSort={onSort}>
                            Fällig
                        </SortableTh>
                        <SortableTh sortKey="gross" sort={sort} onSort={onSort} right>
                            Brutto
                        </SortableTh>
                        <SortableTh sortKey="open" sort={sort} onSort={onSort} right>
                            Offen
                        </SortableTh>
                        <th className="px-5 py-4">Status</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {pageItems.map((inv) => (
                        <tr
                            key={inv.id}
                            // Der Beleg aus der Liste taugt nicht zum
                            // Bearbeiten — seine Positionen sind nicht
                            // geladen. Waehrend des Nachladens ist jeder
                            // weitere Klick gesperrt, sonst laeuft die
                            // Abfrage mehrfach.
                            onClick={() => {
                                if (opening) return;
                                void onOpenInvoice(inv.id);
                            }}
                            aria-busy={opening === inv.id}
                            className={`group transition-colors hover:bg-blue-50/40 ${
                                opening ? 'cursor-wait' : 'cursor-pointer'
                            } ${
                                // Ein stornierter Beleg ist gegenstandslos.
                                // Er bleibt stehen — geloescht wird unter
                                // GoBD nicht —, soll aber nicht mitlesen.
                                inv.status === 'CANCELLED' ? 'text-slate-400' : ''
                            }`}
                        >
                            {/* Nicht mehr die ganze Zelle nowrap, sondern jedes
                                Stueck fuer sich. Grund: mit dem zweiten Chip
                                stieg die Mindestbreite der Tabelle von 1049 auf
                                1106 px — auf einem 1366er Laptop mit
                                aufgeklappter Seitenleiste (260 px) genau ueber
                                die verfuegbare Breite, die Statusspalte waere
                                nur noch ueber den Querlauf erreichbar gewesen.
                                So darf die Zeile bei Enge zwischen Nummer und
                                Chips umbrechen; solange Platz ist, steht alles
                                wie bisher nebeneinander. */}
                            <td className="px-5 py-4 align-middle font-bold text-slate-800">
                                {/* Der Beleg wird geholt — sichtbar, sonst
                                    wirkt der Klick folgenlos. */}
                                <span className="whitespace-nowrap">
                                    {opening === inv.id && (
                                        <Loader2 className="mr-2 inline h-4 w-4 animate-spin text-blue-600 align-[-2px]" />
                                    )}
                                    {inv.invoiceNumber || (
                                        <span className="italic font-medium text-slate-400">
                                            ohne Nummer
                                        </span>
                                    )}
                                </span>
                                {/* Die Belegart nur nennen, wenn sie vom
                                    Normalfall abweicht: ein Storno oder
                                    eine Gutschrift zwischen Rechnungen
                                    ist sonst nicht zu erkennen. */}
                                {DOCUMENT_TYPE_SHORT[inv.documentType] && (
                                    <span className="ml-2 whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                                        {DOCUMENT_TYPE_SHORT[inv.documentType]}
                                    </span>
                                )}
                                {/* Wie der Beleg gerechnet ist. Hier und
                                    nicht in einer eigenen Spalte: die
                                    Angabe gehoert zum Beleg selbst, und
                                    eine neunte Spalte haette die Tabelle
                                    ueber ihre 1000 px hinaus gedehnt.

                                    Zwei Chips treffen nie ueberfluessig
                                    aufeinander: die Belegarten mit
                                    Kuerzel (Abschlag, Storno, Korrektur,
                                    Gutschriften) sind bis auf Schluss-
                                    und Restrechnung genau die, die keine
                                    Abrechnungsart aussagen — dort bleibt
                                    der zweite Chip weg statt „entfällt"
                                    ueber zweihundert Zeilen zu wiederholen. */}
                                <BillingModeChip invoice={inv} className="ml-2" />
                            </td>
                            <td className="px-5 py-4 align-middle">
                                {customerNameOf(inv) || '—'}
                            </td>
                            {/* Der Gedankenstrich ist keine Luecke:
                                Belege aus der Zeit vor dem Projektvermerk
                                tragen schlicht keinen Bezug. */}
                            <td className="px-5 py-4 align-middle">
                                {projectLabelOf(inv) || '—'}
                            </td>
                            <td className="px-5 py-4 align-middle tabular-nums">
                                {de(inv.invoiceDate)}
                            </td>
                            <td className="px-5 py-4 align-middle tabular-nums">
                                {de(inv.dueDate)}
                            </td>
                            {/* whitespace-nowrap: sonst rutscht bei enger Spalte das
                                "€" allein in die naechste Zeile. */}
                            <td className="whitespace-nowrap px-5 py-4 text-right align-middle tabular-nums">
                                {formatCents(inv.totals.grossTotalCents)}
                            </td>
                            <td
                                className="px-5 py-4 text-right align-middle font-bold tabular-nums"
                                // Ohne den Hinweis steht hier eine Zahl, die
                                // kleiner ist als das Brutto daneben, ohne dass
                                // die Liste sagt warum: der Einbehalt steckt im
                                // Zahlbetrag und ist der einzige Abzug, der
                                // nirgends als Beleg erscheint.
                                title={retentionHint(inv)}
                            >
                                {/* openAmountCents statt der generierten Spalte
                                    open_cents: die kann bei Ueberzahlung negativ
                                    werden, und ein negativer offener Posten wird
                                    nie ausgewiesen (Spezifikation §20). */}
                                {/* Der Ueberzahlungs-Hinweis darf umbrechen, der
                                    Betrag selbst nicht — daher pro Betrag ein span. */}
                                <span className="whitespace-nowrap">
                                    {formatCents(openAmountCents(inv))}
                                </span>
                                {inv.paidCents + inv.creditedCents >
                                    inv.totals.payableCents && (
                                    <span className="ml-1 whitespace-nowrap text-xs font-normal text-amber-700">
                                        (+
                                        {formatCents(
                                            inv.paidCents +
                                                inv.creditedCents -
                                                inv.totals.payableCents,
                                        )}
                                        )
                                    </span>
                                )}
                            </td>
                            <td className="px-5 py-4 align-middle">
                                <div className="flex items-center gap-1.5">
                                    <InvoiceStatusBadge invoice={inv} today={today} />
                                    {inv.dunningLevel > 0 && (
                                        <span
                                            title={`Zuletzt gemahnt am ${de(inv.dunningLastAt?.slice(0, 10))}`}
                                            className="rounded-full border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-xs font-medium text-orange-700"
                                        >
                                            {inv.dunningLevel}. Mahnung
                                        </span>
                                    )}
                                    {/* Zahlung buchen, ohne den Beleg zu
                                        oeffnen — der haeufigste Vorgang
                                        nach dem Kontoauszug. Der Knopf
                                        steht nur an Belegen, auf die auch
                                        wirklich gebucht werden kann
                                        (canMarkPaid fragt dieselbe
                                        Pruefung wie das Buchen selbst);
                                        sonst waere er an jeder zweiten
                                        Zeile ein Versprechen, das beim
                                        Klick zurueckgenommen wird.

                                        stopPropagation ist hier keine
                                        Kosmetik: die ganze Zeile oeffnet
                                        den Beleg, und ohne das liefe
                                        hinter dem Dialog noch das
                                        Nachladen der Positionen. */}
                                    {canMarkPaid(inv, today) && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onMarkPaid(inv);
                                            }}
                                            disabled={!zahlungen.erlaubt}
                                            title={
                                                zahlungen.hinweis ??
                                                'Zahlung buchen — als bezahlt markieren'
                                            }
                                            aria-label={`Zahlung buchen für ${
                                                inv.invoiceNumber || 'diesen Beleg'
                                            }`}
                                            className="rounded-full border border-slate-200 p-1 text-slate-400 transition-colors hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                                        >
                                            <Banknote className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
                {/* Die Summe der ANGEZEIGTEN Belege. Bei zweihundert
                    Zeilen ist sie das Einzige, was einen Filter
                    beantwortbar macht: was steht hier zusammen? */}
                <tfoot>
                    <tr className="border-t-2 border-slate-200 bg-slate-50 text-sm font-bold text-slate-700">
                        <td className="px-5 py-3" colSpan={5}>
                            {visibleCount}{' '}
                            {visibleCount === 1 ? 'Beleg' : 'Belege'}
                            {filtersActive && (
                                <span className="ml-2 text-xs font-medium text-slate-400">
                                    gefiltert aus {invoiceCount}
                                </span>
                            )}
                        </td>
                        <td className="whitespace-nowrap px-5 py-3 text-right tabular-nums">
                            {formatCents(totals.gross)}
                        </td>
                        <td className="whitespace-nowrap px-5 py-3 text-right tabular-nums">
                            {formatCents(totals.open)}
                        </td>
                        <td className="px-5 py-3" />
                    </tr>
                </tfoot>
            </table>
            </div>
            {/* Seitenwahl. Bleibt auch bei einer einzigen Seite
                stehen: die Seitengroesse ist dort die eigentliche
                Einstellung, und ein Bedienelement, das je nach
                Trefferzahl verschwindet, sucht man beim naechsten Mal. */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-3 text-xs text-slate-500">
                <div className="flex items-center gap-2">
                    <label htmlFor="invoice-page-size">Pro Seite</label>
                    <select
                        id="invoice-page-size"
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        className="rounded-lg border border-slate-200 px-2 py-1 font-medium text-slate-700"
                    >
                        {[25, 50, 100].map((n) => (
                            <option key={n} value={n}>
                                {n}
                            </option>
                        ))}
                    </select>
                    <span className="tabular-nums">
                        {(currentPage - 1) * pageSize + 1}–
                        {Math.min(currentPage * pageSize, visibleCount)} von{' '}
                        {visibleCount}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage <= 1}
                        className="rounded-lg border border-slate-200 px-3 py-1 font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                    >
                        Zurück
                    </button>
                    <span className="tabular-nums">
                        Seite {currentPage} von {pageCount}
                    </span>
                    <button
                        type="button"
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage >= pageCount}
                        className="rounded-lg border border-slate-200 px-3 py-1 font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                    >
                        Weiter
                    </button>
                </div>
            </div>
        </div>
    );
